import React, { forwardRef } from "react";
import { SliderElement, type SliderElementProps } from "react-hook-form-mui";
import { Theme, useTheme } from "@mui/material/styles";
import merge from "lodash.merge";

/**
 * Reusable slider element with Utilifeed styling defaults.
 * Wraps `react-hook-form-mui`'s `SliderElement` and applies consistent
 * styling + handy percentage value formatting.
 */
export type StyledSliderElementProps = SliderElementProps & {
  /**
   * When true, value labels are displayed as percentages (e.g. `60%`).
   * Defaults to false.
   */
  percentageLabel?: boolean;
  /**
   * If `true`, the thumbs can't cross each other.
   * @default true
   */
  disableSwap?: boolean;
};

const defaultSx = (theme: Theme) => ({
  "& .MuiSlider-thumb": {
    width: "4px",
    height: "44px",
    borderRadius: "4px",
    color: theme.palette.primary.main,
  },
  "& .MuiSlider-rail": {
    color: theme.palette.secondary.main,
    height: "16px",
  },
  "& .MuiSlider-track": {
    color: theme.palette.secondary.main,
    opacity: 0.3,
  },
  "& .MuiSlider-markLabel": {
    color: theme.palette.primary.main,
    fontSize: "12px",
    paddingTop: "10px",
  },
  "& .MuiSlider-mark": {
    width: "4px",
    height: "4px",
    borderRadius: "50%",
    backgroundColor: theme.palette.primary.main,
    marginLeft: "-2px",
  },
});

export const StyledSliderElement = forwardRef<HTMLDivElement, StyledSliderElementProps>(
  (
    {
      percentageLabel = false,
      sx,
      valueLabelDisplay = "auto",
      valueLabelFormat,
      disableSwap = true,
      ...rest
    },
    ref
  ) => {
    const theme = useTheme();
    const mergedSx = merge({}, defaultSx(theme), sx);

    const format =
      valueLabelFormat ||
      ((value: number | number[]) => {
        if (percentageLabel && typeof value === "number") {
          return `${value}%`;
        }
        return value;
      });

    return (
      <SliderElement
        {...rest}
        sx={mergedSx}
        valueLabelDisplay={valueLabelDisplay}
        valueLabelFormat={format}
        disableSwap={disableSwap}
        ref={ref}
      />
    );
  }
);

StyledSliderElement.displayName = "StyledSliderElement";
